import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DeleteIcon from '@material-ui/icons/Delete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Grid, Divider, InputAdornment } from "@material-ui/core";
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../actions";

import MyTextField from '../../utils/MyTextField';
import MyDatePicker from '../../utils/MyDatePicker';
import MyTable from '../../utils/MyTable';
import UnoButton from '../../utils/UnoButton';
import ConfirmActionModal from '../../modals/ConfirmActionModal';
import { checkUserIsRole, roles } from '../../utils/user.js';
import { applyCurrencyMask, applyMaskAmount } from '../../utils/utils';
import { getContractValueRowAt } from '../../utils/contractValues';
import { unoTheme } from "../../../assets/styles/unoTheme";
import { ClientContractValuesAPI } from '../../../API/client_contract_values';

import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },

    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
    },

    main: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
        textAlign: 'left',
        overflowY: 'auto',
    },

    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
        textAlign: 'center',
    },

    infoText: {
        fontSize: '0.8rem',
        color: unoTheme.defaultTextColor,
        textAlign: 'center',
        marginTop: theme.spacing(2),
        fontStyle: 'italic',
    },

    bottomButtons: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },

    historySection: {
        marginTop: theme.spacing(1),
        flex: 1,
    },

    historyRow: {
        '& > *': {
            borderBottom: 'unset',
            fontSize: '0.85rem',
        },
        '&:nth-of-type(odd)': {
            backgroundColor: unoTheme.secondaryTuplaTable,
        },
    },

    currentRow: {
        '& > *': {
            fontWeight: 600,
            color: unoTheme.mainColor,
        },
    },

    editingRow: {
        '& > *': {
            backgroundColor: '#eef3ff',
        },
    },

    rightDivBackdrop: {
        backgroundColor: 'white',
        position: 'absolute',
        opacity: 0.7,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        '& .progress': {
            color: unoTheme.mainColor
        }
    },

    hiddenIcon: {
        visibility: 'hidden',
    },

    divider: {
        margin: theme.spacing(2, 0),
    },
}));

function formatDate(dateStr) {
    if (!dateStr) return '-';
    return moment.utc(dateStr).format('DD/MM/YYYY');
}

function formatValue(value) {
    return value !== null && value !== undefined ? applyMaskAmount(value, true) : '-';
}

export default function ClientContractValuesHistory(props) {

    const classes = useStyles();
    const dispatch = useDispatch();

    const { content } = props;
    const clientOnScreen = content?.clientOnScreen;
    const clientId = clientOnScreen?.id;
    const clientName = clientOnScreen?.rpps_name || clientOnScreen?.rpps_name_initial || '';

    const currentUser = useSelector(state => state.currentUser);

    //Manutenção do histórico só pelo pessoal da LEMA; o servidor aplica a mesma restrição
    const canEdit = checkUserIsRole(currentUser, [roles.superadmin, roles.admin, roles.advisor]);

    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [history, setHistory] = useState([]);
    //Qualquer gravação faz a tela de trás recarregar o valor vigente ao fechar o painel
    const [historyChanged, setHistoryChanged] = useState(false);

    //Formulário: sem editingRow é inclusão, com editingRow é edição da linha
    const [editingRow, setEditingRow] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [contractValue, setContractValue] = useState('');

    const [rowToDelete, setRowToDelete] = useState(undefined);

    const currentRow = getContractValueRowAt(history, moment.utc());

    async function loadHistory() {
        if (!clientId) return;

        const response = await ClientContractValuesAPI.listByClient(clientId);
        if (response.success) {
            setHistory(response.body?.rows ?? []);
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: 'Erro ao carregar o histórico de valores de contrato'
            }));
        }
    }

    function clearForm() {
        setEditingRow(null);
        setStartDate(null);
        setContractValue('');
        setSubmitted(false);
    }

    function startEdit(row) {
        if (!canEdit) return;
        setEditingRow(row);
        setStartDate(moment.utc(row.start_date));
        //O pg devolve NUMERIC como "1234.5": o toFixed garante os centavos antes da máscara
        setContractValue(applyCurrencyMask(Number(row.contract_value).toFixed(2)));
        setSubmitted(false);
    }

    function showAlert(type, message) {
        dispatch(allActions.mainAlertActions.setMainAlert({ show: true, type, message }));
    }

    async function handleSave() {
        setSubmitted(true);

        const validDate = startDate && moment.utc(startDate).isValid();
        if (!validDate || !contractValue) {
            showAlert('warning', 'Informe o início de vigência e o valor');
            return;
        }

        setOpenBackdrop(true);

        const data = {
            contract_value: contractValue,
            start_date: moment.utc(startDate).format('YYYY-MM-DD'),
        };

        const response = editingRow
            ? await ClientContractValuesAPI.update(editingRow.id, data)
            : await ClientContractValuesAPI.create({ ...data, client_id: clientId });

        if (response.success) {
            showAlert('success', editingRow ? 'Valor de contrato atualizado com sucesso!' : 'Valor de contrato cadastrado com sucesso!');
            setHistoryChanged(true);
            clearForm();
            await loadHistory();
        } else {
            showAlert('error', 'Erro ao salvar valor de contrato: ' + (response.body?.message || 'Erro desconhecido'));
        }

        setOpenBackdrop(false);
    }

    async function handleDeleteAction(confirmed) {
        const row = rowToDelete;
        setRowToDelete(undefined);

        if (!confirmed || !row) return;

        setOpenBackdrop(true);

        const response = await ClientContractValuesAPI.remove(row.id);

        if (response.success) {
            if (editingRow && editingRow.id === row.id) {
                clearForm();
            }
            setHistoryChanged(true);
            await loadHistory();
            showAlert('success', 'Valor de contrato excluído com sucesso!');
        } else {
            showAlert('error', 'Erro ao excluir valor de contrato: ' + (response.body?.message || 'Erro desconhecido'));
        }

        setOpenBackdrop(false);
    }

    function getDeleteMessage() {
        if (!rowToDelete) return '';

        return (
            <span>
                {'Tem certeza de que deseja excluir o valor de '}
                <strong>{formatValue(rowToDelete.contract_value)}</strong>
                {' com início de vigência em '}
                <strong>{formatDate(rowToDelete.start_date)}</strong>
                {'?'}
            </span>
        );
    }

    function handleClose() {
        if (historyChanged) {
            dispatch(allActions.rightContentActions.update());
        } else {
            dispatch(allActions.rightContentActions.setRightContent(null));
        }
    }

    useEffect(() => {
        loadHistory();
    }, [clientId]);

    const columnsCount = canEdit ? 3 : 2;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <IconButton className={classes.hiddenIcon}>
                    <ArrowBackIcon />
                </IconButton>

                <div style={{ textAlign: 'center' }}>
                    <Typography variant={'h6'}>
                        {'Histórico do Valor de Contrato'}
                    </Typography>
                    {
                        clientName ?
                            <Typography variant={'subtitle2'} style={{ color: unoTheme.defaultTextColor, textTransform: 'uppercase' }}>
                                {clientName}
                            </Typography> : null
                    }
                </div>

                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </div>

            <div className={classes.main}>

                {
                    canEdit ?
                        <>
                            <Typography className={classes.sectionTitle} variant="subtitle1">
                                {editingRow ? 'Editar Valor' : 'Novo Valor'}
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <MyDatePicker
                                        outerlabel="Início de vigência"
                                        size="small"
                                        format="DD/MM/YYYY"
                                        value={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        fullWidth
                                        autoOk
                                        placeholder="DD/MM/AAAA"
                                        validator={!startDate && submitted}
                                        helperText="Informe a data"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <MyTextField
                                        id="contract-value"
                                        outerlabel="Valor mensal"
                                        align={'right'}
                                        startadornment={<InputAdornment position="start">R$</InputAdornment>}
                                        value={contractValue}
                                        onChange={(value) => setContractValue(value)}
                                        placeholder="0,00"
                                        size="small"
                                        fullWidth
                                        mask={["currency"]}
                                        validator={!contractValue && submitted ? true : undefined}
                                        helperText="Informe o valor"
                                    />
                                </Grid>
                            </Grid>

                            <Typography className={classes.infoText}>
                                Obs.: O valor vale a partir do início de vigência até o próximo início de vigência cadastrado.
                            </Typography>

                            <div className={classes.bottomButtons}>
                                {
                                    editingRow ?
                                        <UnoButton
                                            onClick={clearForm}
                                            type='default'>
                                            Cancelar edição
                                        </UnoButton> : null
                                }
                                <UnoButton
                                    onClick={handleSave}
                                    type='primary'
                                    variant="contained">
                                    {editingRow ? 'Salvar alteração' : 'Adicionar'}
                                </UnoButton>
                            </div>

                            <Divider className={classes.divider} />
                        </> : null
                }

                <div className={classes.historySection}>
                    <Typography className={classes.sectionTitle} variant="subtitle1">
                        Histórico
                    </Typography>

                    <MyTable
                        size="small"
                        sizeheaders="small"
                        stickyHeader
                        selectable={canEdit ? "true" : undefined}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Início de vigência</TableCell>
                                <TableCell align="right">Valor mensal</TableCell>
                                {canEdit ? <TableCell align="right"></TableCell> : null}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.length > 0 ? (
                                history.map((row) => {
                                    const isCurrent = currentRow && currentRow.id === row.id;
                                    const isEditing = editingRow && editingRow.id === row.id;
                                    return (
                                        <TableRow
                                            key={'contract-value-' + row.id}
                                            className={[
                                                classes.historyRow,
                                                isCurrent ? classes.currentRow : '',
                                                isEditing ? classes.editingRow : '',
                                            ].join(' ')}
                                            onClick={() => startEdit(row)}>
                                            <TableCell align="left">
                                                {formatDate(row.start_date)}
                                                {isCurrent ? ' (vigente)' : ''}
                                            </TableCell>
                                            <TableCell align="right">{formatValue(row.contract_value)}</TableCell>
                                            {
                                                canEdit ?
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            style={{ color: unoTheme.redMoney }}
                                                            onClick={(e) => {
                                                                //Não abre a edição da linha
                                                                e.stopPropagation();
                                                                setRowToDelete(row);
                                                            }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                    : null
                                            }
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columnsCount} align="center" style={{ padding: '16px', color: unoTheme.defaultTextColor }}>
                                        Nenhum valor de contrato cadastrado
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </MyTable>
                </div>

            </div>

            {openBackdrop ? (
                <div className={classes.rightDivBackdrop}>
                    <CircularProgress className={'progress'} />
                </div>
            ) : null}

            <ConfirmActionModal
                open={rowToDelete !== undefined}
                title={'Excluir valor de contrato'}
                message={getDeleteMessage()}
                confirmText={'Excluir'}
                handleAction={(confirmed) => handleDeleteAction(confirmed)} />
        </div>
    );
}
